import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { runLoading } from '../../../components/loading';

export interface Country { id: string; longName: string | null; shortName: string | null; chineseLongName: string | null; chineseShortName: string | null; nationalFlag: string | null; }
export interface StateRow { id: string; longName: string | null; shortName: string | null; countryId: string | null; country: string | null; }
export interface Department { id: string; name: string | null; sort: number | null; parentId: string | null; parent: string | null; }
export interface Position { id: string; positionName: string | null; sort: number | null; isDivisionBased: boolean; }
export interface TimeZoneRow { id: string; timeZone: string | null; utc: string | null; active: boolean; }
export interface Language { id: string; chinese: boolean; english: boolean; }
export interface LookupOption { id: string; name: string; }

const K = {
  countries: ['sm-countries'], states: ['sm-states-full'], stateCountries: ['sm-state-countries'],
  departments: ['sm-departments'], positions: ['sm-positions'], timezones: ['sm-timezones'], languages: ['sm-languages'],
};

function list<T>(key: readonly unknown[], url: string, staleMs?: number) {
  return useQuery({ queryKey: key, staleTime: staleMs, queryFn: async () => (await api.get<{ data: T[] }>(url)).data.data });
}
function del(key: readonly unknown[], base: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => runLoading(api.delete(`${base}/${id}`), { blocking: true, label: 'Deleting…' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ── Country ──────────────────────────────────────────────────────────────────
export const useCountries = () => list<Country>(K.countries, '/api/v1/system/countries');
export function useSaveCountry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; longName: string; shortName: string | null; chineseLongName: string | null; chineseShortName: string | null; nationalFlag?: string | null }) => {
      const body = { longName: v.longName, shortName: v.shortName, chineseLongName: v.chineseLongName, chineseShortName: v.chineseShortName, nationalFlag: v.nationalFlag ?? null };
      return v.id ? api.put(`/api/v1/system/countries/${v.id}`, body) : api.post('/api/v1/system/countries', body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.countries }); qc.invalidateQueries({ queryKey: K.stateCountries }); qc.invalidateQueries({ queryKey: K.states }); },
  });
}
export const useDeleteCountry = () => del(K.countries, '/api/v1/system/countries');

// ── State ────────────────────────────────────────────────────────────────────
export const useStates = () => list<StateRow>(K.states, '/api/v1/system/states');
export const useStateCountries = () => list<LookupOption>(K.stateCountries, '/api/v1/system/states/countries', 5 * 60_000);
export function useSaveState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; longName: string; shortName: string | null; countryId: string | null }) => {
      const body = { longName: v.longName, shortName: v.shortName, countryId: v.countryId };
      const res = v.id ? await api.put(`/api/v1/system/states/${v.id}`, body) : await api.post('/api/v1/system/states', body);
      return (res.data as { data?: { id?: string } })?.data?.id ?? v.id ?? null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.states });
      // The State lookup is cached under TWO keys — 'sm-states-full' here and
      // 'sm-states' in city.ts (the City popup's State dropdown). Refresh both, or
      // a rename shows in one list and stays stale in the other.
      qc.invalidateQueries({ queryKey: ['sm-states'] });
      qc.invalidateQueries({ queryKey: ['sm-cities'] });
    },
  });
}
export const useDeleteState = () => del(K.states, '/api/v1/system/states');

// ── Department ─────────────────────────────────────────────────────────────────
export const useDepartments = () => list<Department>(K.departments, '/api/v1/system/departments');
/** Parent dropdown choices for a department: EVERY department, A–Z by name, exactly as the legacy
 *  lookup lists them — including the department being edited (owner request 2026-10-12; legacy
 *  offers it too). Picking itself is refused on save with the legacy circular-reference message. */
export function departmentParentOptions(all: Department[]): { value: string; label: string }[] {
  return all
    .map((d) => ({ value: d.id, label: d.name ?? '' }))
    .sort((x, y) => x.label.localeCompare(y.label, undefined, { sensitivity: 'base' }));
}

export function useSaveDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; name: string; sort: number | null; parentId: string | null }) => {
      const body = { name: v.name, sort: v.sort, parentId: v.parentId };
      const res = v.id ? await api.put(`/api/v1/system/departments/${v.id}`, body) : await api.post('/api/v1/system/departments', body);
      return (res.data as { data?: { id?: string } })?.data?.id ?? v.id ?? null;   // new/updated department Oid
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: K.departments }),
  });
}
export const useDeleteDepartment = () => del(K.departments, '/api/v1/system/departments');

// ── Department ▸ Employees tab (Employee.Department FK) ───────────────────────────
export interface DeptEmployee {
  id: string; displayName: string | null; fullName: string | null; userName: string | null;
  jobTitle: string | null; position: string | null; role: string | null;
  email: string | null; mobilePhone: string | null; officePhone: string | null;
  employmentDate: string | null; gender: number | null; isActive: boolean; changePasswordOnFirstLogon: boolean;
  // Wider LIMS Department_Employees_ListView columns (Password excluded for security).
  state: string | null; city: string | null; birthday: string | null; initial: string | null;
  reportingTo: string | null; isManager: boolean; mailServerName: string | null; port: number | null;
  enableSSL: boolean; sampleRegistrationDefault: boolean; reportDeliveryDefault: boolean;
  invoiceDeliveryDefault: boolean; suboutNotificationDefault: boolean; department: string | null;
  isLIMSAdministrator: boolean; activityStart: string | null;   // showAll = IsLIMSAdministrator; StartDat = Activity.StartDateOn
}
/** Full Employee detail — read for the Edit form, written back on Save (Guids as strings). */
export interface EmployeeDetail {
  id: string; displayName: string | null; userName: string | null; isActive: boolean; changePasswordOnFirstLogon: boolean;
  firstName: string | null; lastName: string | null; initial: string | null; jobTitle: string | null;
  gender: number | null; birthday: string | null;
  email: string | null; mobilePhone: string | null; officePhone: string | null; homePhone: string | null;
  fax: string | null; otherPhone: string | null; address: string | null; zip: string | null;
  company: string | null; department: string | null; position: string | null; role: string | null;
  country: string | null; state: string | null; city: string | null;
  employmentDate: string | null; retireDate: string | null; language: number | null; isEnabledLanguage: boolean;
  isManager: boolean; isLabUser: boolean; isLIMSAdministrator: boolean; resume: string | null;
  photo: string | null; signature: string | null;   // base64 (varbinary on dbo.Employee)
  deliveryPassword: string | null;   // Contact "Delivery Password" = dbo.Employee.Password (email-delivery credential)
}
const deptEmpKey = (id: string) => ['sm-dept-employees', id];
const deptEmpAvailKey = (id: string) => ['sm-dept-employees-avail', id];
export function useDeptEmployees(deptId?: string) {
  return useQuery({ queryKey: deptEmpKey(deptId ?? ''), enabled: !!deptId,
    queryFn: async () => (await api.get<{ data: DeptEmployee[] }>(`/api/v1/system/departments/${deptId}/employees`)).data.data });
}
export function useAvailableEmployees(deptId?: string) {
  return useQuery({ queryKey: deptEmpAvailKey(deptId ?? ''), enabled: !!deptId,
    queryFn: async () => (await api.get<{ data: DeptEmployee[] }>(`/api/v1/system/departments/${deptId}/employees/available`)).data.data });
}
export function useLinkEmployee(deptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (employeeId: string) => api.put(`/api/v1/system/departments/${deptId}/employees/${employeeId}`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: deptEmpKey(deptId) }); qc.invalidateQueries({ queryKey: deptEmpAvailKey(deptId) }); },
  });
}
export function useUnlinkEmployee(deptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (employeeId: string) => api.delete(`/api/v1/system/departments/${deptId}/employees/${employeeId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: deptEmpKey(deptId) }); qc.invalidateQueries({ queryKey: deptEmpAvailKey(deptId) }); },
  });
}
// Full Employee CRUD (the detail form opened from the Department ▸ Employees tab).
export function useEmployee(id?: string) {
  return useQuery({ queryKey: ['sm-employee', id], enabled: !!id,
    queryFn: async () => (await api.get<{ data: EmployeeDetail }>(`/api/v1/system/employees/${id}`)).data.data });
}
export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (e: EmployeeDetail) => api.put(`/api/v1/system/employees/${e.id}`, e),
    onSuccess: (_d, e) => { qc.invalidateQueries({ queryKey: ['sm-dept-employees'] }); qc.invalidateQueries({ queryKey: ['sm-dept-employees-avail'] }); qc.invalidateQueries({ queryKey: ['sm-employee', e.id] }); },
  });
}
export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => runLoading(api.delete(`/api/v1/system/employees/${id}`), { blocking: true, label: 'Deleting…' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sm-dept-employees'] }); qc.invalidateQueries({ queryKey: ['sm-dept-employees-avail'] }); },
  });
}
export function useResetEmployeePassword() {
  return useMutation({
    mutationFn: async (v: { id: string; newPassword: string; confirmPassword: string }) =>
      api.put(`/api/v1/system/employees/${v.id}/password`, { newPassword: v.newPassword, confirmPassword: v.confirmPassword }),
  });
}
export const useCompanyOptions = () => list<LookupOption>(['sm-companies'], '/api/v1/system/employees/companies', 5 * 60_000);
export const useRoleOptions = () => list<LookupOption>(['sm-roles'], '/api/v1/system/employees/roles', 5 * 60_000);

// ── Company (Division) — Basic Information + Contact ──────────────────────────────
export interface Company {
  id: string; name: string | null; officePhone: string | null; address: string | null;
  city: string | null; state: string | null; zip: string | null; country: string | null;
  department: string | null; timeZone: string | null; utc: string | null;
}
export interface CompanyDetail {
  id: string; companyName: string | null; webSite: string | null; address: string | null; language: number | null;
  country: string | null; state: string | null; city: string | null; isValid: boolean; remark: string | null;
  contactName: string | null; officePhone: string | null; mobilePhone: string | null; fax: string | null;
  homePhone: string | null; otherPhone: string | null; email: string | null;
  zip: string | null; timeZone: string | null;   // Zip + TimeZone FK (edited via the grid inline-edit row)
  logo: string | null; qrCode: string | null; measureCvFlag: string | null;   // image tabs — base64 (byte[] on the API)
  // SMTP Configuration tab. fromMailPassword is AES-encrypted at rest; the API returns a sentinel
  // (masked dots) when set and re-encrypts only when the user types a new value.
  enableSmtp: boolean; mailServer: string | null; portNo: number | null; ssl: boolean;
  fromMail: string | null; fromMailPassword: string | null; displayName: string | null;
}
const COMPANY_KEY = ['sm-companies-list'];
export const useCompanies = () => list<Company>(COMPANY_KEY, '/api/v1/system/companies');
export function useCompany(id?: string) {
  return useQuery({ queryKey: ['sm-company', id], enabled: !!id,
    queryFn: async () => (await api.get<{ data: CompanyDetail }>(`/api/v1/system/companies/${id}`)).data.data });
}
export function useSaveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: CompanyDetail) => {
      const { id, ...body } = v;
      const res = id ? await api.put(`/api/v1/system/companies/${id}`, body) : await api.post('/api/v1/system/companies', body);
      return (res.data as { data?: { id?: string } })?.data?.id ?? id ?? null;
    },
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: COMPANY_KEY }); qc.invalidateQueries({ queryKey: ['sm-company', v.id] }); qc.invalidateQueries({ queryKey: ['sm-companies'] }); },
  });
}
export const useDeleteCompany = () => del(COMPANY_KEY, '/api/v1/system/companies');
/** What Test Mail posts: the SMTP Configuration fields as they are ON SCREEN, plus the Test Mail
 * panel's recipient and message. `fromMailPassword` is whatever the box holds — the load sentinel
 * (server reuses the stored password while Mail Server + From Mail are unchanged) or a newly typed
 * password. */
export type SmtpTestInput =
  Pick<CompanyDetail, 'mailServer' | 'portNo' | 'ssl' | 'fromMail' | 'fromMailPassword' | 'displayName'>
  & { toEmail: string; message: string };

/** Test Mail result. On failure `message` is a plain headline ("Authentication failed.") and
 * `detail` the server's own reply, naming the server that was tried. */
export interface SmtpTestResult { ok: boolean; message: string; detail?: string }

// SMTP Configuration ▸ Test Mail — sends a real email, using the values ON SCREEN (saved or not), to
// the ONE address typed in the panel. Admin/service only (enforced by the API). Without a companyId
// (division not saved yet) the id-less endpoint is used and the password must be typed.
export function useTestSmtp(companyId: string) {
  return useMutation({
    mutationFn: async (input: SmtpTestInput) => {
      const url = companyId ? `/api/v1/system/companies/${companyId}/smtp/test` : '/api/v1/system/companies/smtp/test';
      return (await api.post<{ data: SmtpTestResult }>(url, input)).data.data;
    },
  });
}

// Company ▸ Departments tab (M2M dbo.CompanyDepartment). The Link picker source is the full
// Department list (useDepartments); these manage the links for one division.
const divDeptKey = (id: string) => ['sm-division-departments', id];
export function useDivisionDepartments(companyId?: string) {
  return useQuery({ queryKey: divDeptKey(companyId ?? ''), enabled: !!companyId,
    queryFn: async () => (await api.get<{ data: Department[] }>(`/api/v1/system/companies/${companyId}/departments`)).data.data });
}
// `source` audits the change under the right FormName ("Division Department" vs "Division" — §14b.2).
export function useLinkDivisionDepartment(companyId: string, source?: string) {
  const qc = useQueryClient();
  const q = source ? `?source=${source}` : '';
  return useMutation({
    mutationFn: async (departmentId: string) => api.put(`/api/v1/system/companies/${companyId}/departments/${departmentId}${q}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: divDeptKey(companyId) }),
  });
}
export function useUnlinkDivisionDepartment(companyId: string, source?: string) {
  const qc = useQueryClient();
  const q = source ? `?source=${source}` : '';
  return useMutation({
    mutationFn: async (departmentId: string) => api.delete(`/api/v1/system/companies/${companyId}/departments/${departmentId}${q}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: divDeptKey(companyId) }),
  });
}

// Company ▸ Positions tab (one-to-many Position.Company). Picker source = usePositions (all).
const divPosKey = (id: string) => ['sm-division-positions', id];
export function useDivisionPositions(companyId?: string) {
  return useQuery({ queryKey: divPosKey(companyId ?? ''), enabled: !!companyId,
    queryFn: async () => (await api.get<{ data: Position[] }>(`/api/v1/system/companies/${companyId}/positions`)).data.data });
}
export function useLinkDivisionPosition(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (positionId: string) => api.put(`/api/v1/system/companies/${companyId}/positions/${positionId}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: divPosKey(companyId) }),
  });
}
export function useUnlinkDivisionPosition(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (positionId: string) => api.delete(`/api/v1/system/companies/${companyId}/positions/${positionId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: divPosKey(companyId) }),
  });
}

// Company ▸ Certificate tab (one-to-many child dbo.Certificates) — New/Edit/Delete CRUD.
export interface CertificateRow {
  id: string; item: string | null; text: string | null; expirationDate: string | null;
  comment: string | null; lastUpdatedDate: string | null; lastUpdatedBy: string | null;
}
const divCertKey = (id: string) => ['sm-division-certificates', id];
export function useDivisionCertificates(companyId?: string) {
  return useQuery({ queryKey: divCertKey(companyId ?? ''), enabled: !!companyId,
    queryFn: async () => (await api.get<{ data: CertificateRow[] }>(`/api/v1/system/companies/${companyId}/certificates`)).data.data });
}
export function useSaveCertificate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; item: string | null; text: string | null; expirationDate: string | null; comment: string | null }) => {
      const body = { item: v.item, text: v.text, expirationDate: v.expirationDate, comment: v.comment };
      return v.id ? api.put(`/api/v1/system/companies/${companyId}/certificates/${v.id}`, body) : api.post(`/api/v1/system/companies/${companyId}/certificates`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: divCertKey(companyId) }),
  });
}
export function useDeleteCertificate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => runLoading(api.delete(`/api/v1/system/companies/${companyId}/certificates/${certId}`), { blocking: true, label: 'Deleting…' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: divCertKey(companyId) }),
  });
}
// Certificate Link/Unlink (LIMS parity). Linkable = orphan certificates (not attached to any division).
export function useLinkableCertificates(companyId?: string, enabled = true) {
  return useQuery({ queryKey: ['sm-certificates-linkable', companyId ?? ''], enabled: !!companyId && enabled,
    queryFn: async () => (await api.get<{ data: CertificateRow[] }>(`/api/v1/system/companies/${companyId}/certificates/available`)).data.data });
}
export function useLinkCertificate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => api.put(`/api/v1/system/companies/${companyId}/certificates/${certId}/link`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: divCertKey(companyId) }); qc.invalidateQueries({ queryKey: ['sm-certificates-linkable'] }); },
  });
}
export function useUnlinkCertificate(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => api.delete(`/api/v1/system/companies/${companyId}/certificates/${certId}/link`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: divCertKey(companyId) }); qc.invalidateQueries({ queryKey: ['sm-certificates-linkable'] }); },
  });
}

// ── Position ───────────────────────────────────────────────────────────────────
export const usePositions = () => list<Position>(K.positions, '/api/v1/system/positions');
export function useSavePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; positionName: string; sort: number | null; isDivisionBased: boolean }) => {
      const body = { positionName: v.positionName, sort: v.sort, isDivisionBased: v.isDivisionBased };
      const res = v.id ? await api.put(`/api/v1/system/positions/${v.id}`, body) : await api.post('/api/v1/system/positions', body);
      return (res.data as { data?: { id?: string } })?.data?.id ?? v.id ?? null;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.positions }); qc.invalidateQueries({ queryKey: ['sm-division-positions'] }); },
  });
}
export const useDeletePosition = () => del(K.positions, '/api/v1/system/positions');

// ── Read-only reference lists ───────────────────────────────────────────────────
export const useTimeZones = () => list<TimeZoneRow>(K.timezones, '/api/v1/system/timezones');
/** Toggle a Time Zone's Active flag inline (TimeZone + UTC are read-only — LIMS TimeZones_ListView). */
export function useUpdateTimeZoneActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; active: boolean }) => api.put(`/api/v1/system/timezones/${v.id}`, { active: v.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.timezones }),
  });
}
export const useLanguages = () => list<Language>(K.languages, '/api/v1/system/languages');
/** Inline-edit a Current Language row's Chinese / English toggles (LIMS CurrentLanguage — editable). */
export function useUpdateLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; chinese: boolean; english: boolean }) => api.put(`/api/v1/system/languages/${v.id}`, { chinese: v.chinese, english: v.english }),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.languages }),
  });
}

// ── Glossary (dbo.Glossary) — LIMS Glossary_ListView: read-only list, inline Definition edit (batch) ──
export interface GlossaryRow {
  id: string; fieldId: string | null; fieldName: string | null; acronym: string | null;
  definition: string | null; tableName: string | null; module: string | null; bo: string | null;
}
export const useGlossary = () => list<GlossaryRow>(['sm-glossary'], '/api/v1/system/glossary', 60_000);
/** Save one Glossary row's Definition (the only editable field). Page batches these, then invalidates. */
export function useUpdateGlossaryDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; definition: string | null }) => api.put(`/api/v1/system/glossary/${v.id}/definition`, { definition: v.definition }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-glossary'] }),
  });
}
/** Glossary detail form (LIMS Glossary_DetailView) — Acronym + Definition are the only editable fields. */
export function useSaveGlossaryDetail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; acronym: string | null; definition: string | null }) =>
      api.put(`/api/v1/system/glossary/${v.id}`, { acronym: v.acronym, definition: v.definition }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-glossary'] }),
  });
}

// ── Glossary ▸ Translations child grid (dbo.Translations) ──
export interface GlossaryTranslation { id: string; language: number; translation: string | null; }
export const GLOSSARY_LANGUAGES = [{ v: 0, t: 'Chinese' }, { v: 1, t: 'French' }, { v: 2, t: 'Spanish' }];
export function useGlossaryTranslations(glossaryId?: string) {
  return useQuery({
    queryKey: ['sm-glossary-translations', glossaryId],
    enabled: !!glossaryId,
    queryFn: async () => (await api.get<{ data: GlossaryTranslation[] }>(`/api/v1/system/glossary/${glossaryId}/translations`)).data.data,
  });
}
export function useSaveGlossaryTranslation(glossaryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; language: number; translation: string }) =>
      v.id
        ? api.put(`/api/v1/system/glossary/translations/${v.id}?glossaryId=${glossaryId}`, { language: v.language, translation: v.translation })
        : api.post(`/api/v1/system/glossary/${glossaryId}/translations`, { language: v.language, translation: v.translation }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-glossary-translations', glossaryId] }),
  });
}
export function useDeleteGlossaryTranslation(glossaryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/system/glossary/translations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-glossary-translations', glossaryId] }),
  });
}

// ── Message Editor (dbo.MessageEditor) — LIMS MessageEditor_ListView (CRUD) ──
export interface MessageEditorRow {
  id: string; msid: string | null; message: string | null; messageKey: string | null;
  actionType: number; module: string | null; bo: string | null; remark: string | null;
}
export interface MessageEditorDetail {
  id: string; msid: string | null; message: string | null; messageKey: string | null;
  moduleId: string | null; boId: string | null; actionType: number; remark: string | null;
}
export const ACTION_TYPES = [{ v: 0, t: 'Simple action' }, { v: 1, t: 'Single choice action' }, { v: 2, t: 'Parameterized action' }];

export const useMessageEditors = () => list<MessageEditorRow>(['sm-message-editor'], '/api/v1/system/message-editor', 30_000);
export function useMessageEditor(id?: string) {
  return useQuery({
    queryKey: ['sm-message-editor-detail', id],
    enabled: !!id,
    queryFn: async () => (await api.get<{ data: MessageEditorDetail }>(`/api/v1/system/message-editor/${id}`)).data.data,
  });
}
export const useMessageModules = () => list<LookupOption>(['sm-message-modules'], '/api/v1/system/message-editor/modules', 300_000);
export function useMessageBos(moduleId?: string | null) {
  return useQuery({
    queryKey: ['sm-message-bos', moduleId],
    enabled: !!moduleId,
    queryFn: async () => (await api.get<{ data: LookupOption[] }>(`/api/v1/system/message-editor/modules/${moduleId}/bos`)).data.data,
  });
}
export function useSaveMessageEditor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; message: string | null; messageKey: string | null; moduleId: string | null; boId: string | null; actionType: number; remark: string | null }) => {
      const body = { message: v.message, messageKey: v.messageKey, moduleId: v.moduleId, boId: v.boId, actionType: v.actionType, remark: v.remark };
      return v.id ? api.put(`/api/v1/system/message-editor/${v.id}`, body) : api.post('/api/v1/system/message-editor', body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-message-editor'] }),
  });
}
export const useDeleteMessageEditor = () => del(['sm-message-editor'], '/api/v1/system/message-editor');

// Message Editor ▸ Translations (same shape as Glossary translations, MessageEditor FK)
export function useMessageTranslations(messageId?: string) {
  return useQuery({
    queryKey: ['sm-message-translations', messageId],
    enabled: !!messageId,
    queryFn: async () => (await api.get<{ data: GlossaryTranslation[] }>(`/api/v1/system/message-editor/${messageId}/translations`)).data.data,
  });
}
export function useSaveMessageTranslation(messageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; language: number; translation: string }) =>
      v.id
        ? api.put(`/api/v1/system/message-editor/translations/${v.id}?messageId=${messageId}`, { language: v.language, translation: v.translation })
        : api.post(`/api/v1/system/message-editor/${messageId}/translations`, { language: v.language, translation: v.translation }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-message-translations', messageId] }),
  });
}
export function useDeleteMessageTranslation(messageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/system/message-editor/translations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-message-translations', messageId] }),
  });
}

export function errMessage(e: unknown, fallback = 'Something went wrong.'): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? fallback;
}

// ── Division ▸ New: tab changes held until the division exists (G5 parity, 2026-10-14) ──────────────────
/** The orphan-certificate list ignores the division id, so a not-yet-saved division asks with this one. */
export const NO_COMPANY_ID = '00000000-0000-0000-0000-000000000000';
/** A certificate typed on a not-yet-saved division — created once the division is saved. */
export interface CertificateDraft { key: string; item: string | null; text: string | null; expirationDate: string | null; comment: string | null }
/** Everything the three tabs hold for a not-yet-saved division. */
export interface DivisionStaging { departmentIds: string[]; positionIds: string[]; certificateLinkIds: string[]; certificateDrafts: CertificateDraft[] }
export const EMPTY_DIVISION_STAGING: DivisionStaging = { departmentIds: [], positionIds: [], certificateLinkIds: [], certificateDrafts: [] };
export const hasDivisionStaging = (st: DivisionStaging) =>
  st.departmentIds.length + st.positionIds.length + st.certificateLinkIds.length + st.certificateDrafts.length > 0;
/** Applies the held tab changes to the division just created — through the same endpoints the tabs use once
 *  the division exists (each one audited server-side as a Division update). */
export async function commitDivisionStaging(companyId: string, st: DivisionStaging): Promise<void> {
  const base = `/api/v1/system/companies/${companyId}`;
  for (const id of st.departmentIds) await api.put(`${base}/departments/${id}`, {});
  for (const id of st.positionIds) await api.put(`${base}/positions/${id}`, {});
  for (const id of st.certificateLinkIds) await api.put(`${base}/certificates/${id}/link`, {});
  for (const d of st.certificateDrafts)
    await api.post(`${base}/certificates`, { item: d.item, text: d.text, expirationDate: d.expirationDate, comment: d.comment });
}
